import { FunctionComponent } from 'preact';
import { useEffect, useCallback } from 'preact/hooks';
import type { StreamProvider } from '@vgtv/api-client';

import { Typography } from '../../components/Typography/Typography';
import { useViewportType } from '../../hooks/useViewportType';
import { Button } from '../../components/Button/Button';
import { useSchibstedAccount } from '../../../../utils/SchibstedAccountProvider';
import { useProvider } from '../../hooks/useProvider';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';
import { useNoPlayback } from '../../hooks/useNoPlayback';
import type { SchibstedAccount } from '../../../../config';
import { setEidFlowState } from '../../services/eid/eidFlow';
import { usePulse } from '../../hooks/PulseProvider';
import { countryMap } from '../../../../config/countryMap';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';

function getLoginParams(
  provider: StreamProvider,
  schAccEnv: SchibstedAccount['env']
) {
  const state =
    provider === 'vgtv' ? window.location.pathname : setEidFlowState();

  if (schAccEnv === 'PRE') {
    const providerCountry = Object.keys(countryMap).find((k) =>
      countryMap[k].includes(provider)
    );
    return { acrValues: `eid-${providerCountry}`, state };
  }

  return { acrValues: 'eid', state };
}

export const VerificationLayout: FunctionComponent = () => {
  const { isMobile } = useViewportType();
  const { configProvider } = useProvider();
  const { login, env } = useSchibstedAccount();
  const { asset } = useCurrentAsset();
  const isAssetFree = Object.keys(asset?.additional?.access || {}).length < 1;

  useNoPlayback(true);

  const { t } = useTranslation(ns);

  const pulse = usePulse();

  const trackGeoblockView = useCallback(() => {
    if (isAssetFree) return;
    pulse.track('viewEvent', {
      object: {
        type: 'UIElement',
        id: `geoblock`,
      },
    });
  }, [isAssetFree]);

  const trackVerifyClick = useCallback(() => {
    pulse.track('engagementEvent', {
      action: 'Click',
      object: {
        type: 'UIElement',
        id: `geoblock:verify`,
      },
    });
  }, []);

  const onVerifyClick = useCallback(() => {
    trackVerifyClick();
    login(getLoginParams(configProvider, env));
  }, [login, env, configProvider, trackVerifyClick]);

  useEffect(() => {
    trackGeoblockView();
  }, []);

  return (
    <div className="vgp-bankid-layout">
      <div className="vgp-bankid-content">
        <Typography variant="label" size="large" color="white">
          {t('You are abroad')}
        </Typography>
        {!isAssetFree && (
          <>
            <Typography variant="label" size="medium" color="white">
              {t('Verification message')}
            </Typography>
            <Button
              size={isMobile ? 'medium' : 'large'}
              onClick={onVerifyClick}
            >
              {t('Verify with BankID')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
